import * as React from "react";
import { Fab, Typography, makeStyles } from "@material-ui/core";
import * as qs from "query-string";
import { useTranslation } from "react-i18next";
import { Link } from "src/common/components";
import { IconArrowDropDown } from "src/common/icons";
import {
  ITalk,
  ITalkQuestionnaire,
  OpenTasksSubmissionStatus,
  PeerSelectionStatus,
  QuestionnaireType,
  SubmissionStatus,
  TalkStatus
} from "src/common/types";
import { dateWithoutTime, displayDate, getFullName } from "src/helpers";
import DateStatus from "../DateStatus";
import Status from "../EmployeeCard/status";

const useStyles = makeStyles((theme: any) => ({
  arrow: {
    display: "inline-block" as "inline-block",
    fontSize: "1.35rem",
    lineHeight: "1",
    transform: "rotate(360deg) scale(0.9,1.35)",
    transition: "transform 0.2s linear",
    verticalAlign: "middle" as "middle"
  },
  dateWrapper: {
    color: "#707070",
    display: "none" as "none",
    minWidth: 124,
    padding: "0 8px",
    width: 124,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  expandButton: {
    background: "none" as "none",
    boxShadow: "none" as "none"
  },
  expandButtonLabel: {
    height: "100%"
  },
  expandLabel: {
    alignItems: "center" as "center",
    cursor: "pointer" as "pointer",
    display: "flex" as "flex",
    height: "40px",
    justifyContent: "center" as "center",
    width: "40px"
  },
  expandWrapper: {
    minWidth: 54,
    padding: "0 16px 0 8px",
    width: 54
  },
  feedbackCardAction: {
    "& $expandWrapper": {
      display: "none" as "none",
      [theme.breakpoints.up("md")]: {
        display: "block" as "block"
      }
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between" as "space-between",
    lineHeight: "24px",
    padding: "6px 0",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "flex-start" as "flex-start",
      lineHeight: "60px",
      padidng: "0"
    }
  },
  feedbackCardActionName: {
    display: "block" as "block",
    padding: "0 8px 0 16px",
    width: "100%" as "100%",
    [theme.breakpoints.up("md")]: {
      flexGrow: 1,
      padding: "0 8px 0 0",
      width: "auto" as "auto"
    }
  },
  feedbackCardActions: {
    display: "flex" as "flex",
    flexDirection: "column" as "column",
    maxHeight: 0,
    overflow: "hidden" as "hidden",
    padding: "0",
    transition: ".3s max-height ease-out",
    [theme.breakpoints.up("md")]: {
      padding: "0 0 0 40px"
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0 0 0 85px"
    }
  },
  feedbackCardContainer: {
    background: "#ffffff",
    border: "1px solid #e6e6e6",
    borderRadius: 4,
    boxShadow: "0 2px 4px 0 rgba(219, 219, 219, 0.5)",
    display: "flex" as "flex",
    flexDirection: "column" as "column",
    margin: "12px 0 0"
  },
  feedbackCardCycle: {
    "& $expandWrapper": {
      order: 2,
      [theme.breakpoints.up("md")]: {
        order: "initial" as "initial"
      }
    },
    "& $mobileDates": {
      order: 3
    },
    "& $statusWrapper": {
      order: 4,
      [theme.breakpoints.up("md")]: {
        order: "initial" as "initial"
      }
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between" as "space-between",
    lineHeight: "24px",
    padding: "6px 0",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      lineHeight: "60px",
      padding: "0"
    }
  },
  feedbackCardCycleName: {
    display: "block" as "block",
    flexGrow: 1,
    padding: "0 8px 0 16px",
    width: "calc(100% - 54px)",
    [theme.breakpoints.up("md")]: {
      width: "auto" as "auto"
    }
  },
  mobileDates: {
    display: "block" as "block",
    fontSize: "1.2rem",
    lineHeight: 1,
    maxWidth: "calc(100% - 140px)",
    padding: "0 8px 0 16px",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  respondentWrapper: {
    display: "none" as "none",
    minWidth: 184,
    padding: "0 8px",
    width: 184,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  statusWrapper: {
    alignItems: "center",
    display: "flex",
    lineHeight: 1,
    padding: "0 8px",
    [theme.breakpoints.up("md")]: {
      lineHeight: "inherit" as "inherit",
      minWidth: 184,
      width: 184
    }
  },
  title: {
    display: "inline-block" as "inline-block",
    lineHeight: 1,
    overflowWrap: "break-word" as "break-word",
    verticalAlign: "middle" as "middle",
    wordBreak: "break-word" as "break-word"
  },
  toggler: {
    "&:checked ~ $feedbackCardActions": {
      maxHeight: 999,
      transition: ".3s max-height ease-in"
    },
    "&:checked ~ $feedbackCardCycle $arrow": {
      transform: "rotate(180deg) scale(0.9,1.35)",
      transition: "transform 0.2s linear"
    },
    display: "none" as "none"
  }
}));

interface IOwnProps {
  defaultOpen?: boolean;
  icName: string;
}

type Props = IOwnProps & ITalk;

const FeedbackCard = ({
  id: talkId,
  status: talkStatus,
  talkCompletionDueDate,
  cycle: { name: cycleName, id: cycleId },
  talkQuestionnaires,
  participantSignedDate,
  supervisorSignedDate,
  peerSelection,
  icName
}: Props) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();

  const currentDate = new Date();
  const classes = useStyles();

  const talkCheckboxId = `talk:${talkId}`;

  const {
    firstName: dsvFirstName,
    lastName: dsvLastName,
    middleName: dsvMiddleName
  } = React.useMemo(() => {
    const dsvTemplate = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.DirectSupervisor
    );

    if (dsvTemplate) {
      const {
        feedbackGiver: { firstName, lastName, middleName }
      } = dsvTemplate;
      return {
        firstName,
        lastName,
        middleName
      };
    } else {
      return {
        firstName: "",
        lastName: "",
        middleName: ""
      };
    }
  }, [talkQuestionnaires]);

  const dsvFullName = getFullName([dsvFirstName, dsvMiddleName, dsvLastName]);

  const TalkSheetAction = React.useMemo(() => {
    const completionDatePassed = currentDate > dateWithoutTime(talkCompletionDueDate);

    const talkSignedDate =
      participantSignedDate && supervisorSignedDate
        ? [supervisorSignedDate, supervisorSignedDate].sort((date1, date2) => date2.getTime() - date1.getTime())[0]
        : null;

    const talkDateStatus =
      completionDatePassed && OpenTasksSubmissionStatus.includes(talkStatus)
        ? "warning"
        : completionDatePassed && talkStatus === TalkStatus.Done
        ? "success"
        : "normal";

    const participantQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
    );

    const peerSummaryQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.PeerSummary
    );

    const scheduleTalkVisible = participantQuestionnaire
      ? participantQuestionnaire.submissionStatus === SubmissionStatus.Submitted
      : peerSummaryQuestionnaire
      ? peerSummaryQuestionnaire.submissionStatus === SubmissionStatus.Submitted
      : false;

    const { supportedLanguages = null } =
      participantQuestionnaire && participantQuestionnaire.questionnaireTemplateConfig
        ? JSON.parse(participantQuestionnaire.questionnaireTemplateConfig)
        : {};

    return (
      <div className={classes.feedbackCardCycle}>
        <div className={classes.feedbackCardCycleName}>
          {scheduleTalkVisible ? (
            <Link
              to={`/${language}/dashboard/my-feedbacks/talk-sheet/${talkId}?${btoa(
                qs.stringify(
                  { supported: [supportedLanguages] },
                  {
                    arrayFormat: "comma"
                  }
                )
              )}`}
            >
              {cycleName}
            </Link>
          ) : (
            <Typography className={classes.title} variant="subtitle1" display="inline">
              {cycleName}
            </Typography>
          )}
        </div>
        <div className={classes.mobileDates}>
          {talkStatus === TalkStatus.Done ? (
            <>
              {`${displayDate(talkSignedDate as Date)} / `}
              <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
            </>
          ) : (
            <>
              <Typography variant="body1" display="inline">
                {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
              </Typography>
              <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
            </>
          )}
        </div>
        <div className={classes.statusWrapper}>
          <Status icName={icName} dsvName={dsvFirstName} type="talk" status={talkStatus} />
        </div>
        <div className={classes.respondentWrapper}>
          <Typography className={classes.title} variant="body1" display="inline">
            {talkStatus === TalkStatus.WaitingForDSVSignature
              ? dsvFullName
              : t(`feedback.page.my-feedbacks.respondent.me`)}
          </Typography>
        </div>
        <div className={classes.dateWrapper}>
          <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
        </div>
        <div className={classes.expandWrapper}>
          <Fab
            classes={{ label: classes.expandButtonLabel }}
            className={classes.expandButton}
            size="small"
            color="default"
          >
            <label className={classes.expandLabel} htmlFor={talkCheckboxId}>
              <Typography color="primary" className={classes.arrow}>
                <IconArrowDropDown />
              </Typography>
            </label>
          </Fab>
        </div>
      </div>
    );
  }, [talkQuestionnaires, language]);

  const DSVQuesitonnaireAction = React.useMemo(() => {
    const dsvTemplate = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.DirectSupervisor
    );

    if (dsvTemplate) {
      const { submissionStatus: supervisorSubmissionStatus, submissionDueDate: supervisorSubmissionDueDate } =
        dsvTemplate;

      const submissionDatePassed = currentDate > dateWithoutTime(supervisorSubmissionDueDate);

      const supervisorDateStatus =
        submissionDatePassed && OpenTasksSubmissionStatus.includes(supervisorSubmissionStatus)
          ? "warning"
          : submissionDatePassed && supervisorSubmissionStatus === SubmissionStatus.Submitted
          ? "success"
          : "normal";

      return (
        <div className={classes.feedbackCardAction}>
          <div className={classes.feedbackCardActionName}>
            <Typography className={classes.title} display="inline" variant="subtitle1">
              {`${t(`feedback.submitter.directSupervisor`)} `}
            </Typography>
          </div>
          <div className={classes.mobileDates}>
            <Typography variant="body1" display="inline">
              {`${dsvFullName} `}
            </Typography>
            <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate} />
          </div>
          <div className={classes.statusWrapper}>
            <Status type="questionnaire" status={supervisorSubmissionStatus} />
          </div>
          <div className={classes.respondentWrapper}>
            <Typography className={classes.title} variant="body1" display="inline">
              {dsvFullName}
            </Typography>
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate} />
          </div>
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [talkQuestionnaires, language]);

  const MyQuestionnaireAction = React.useMemo(() => {
    const participantQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
    ) as ITalkQuestionnaire;

    if (participantQuestionnaire) {
      const {
        id: questionnaireId,
        submissionStatus: participantSubmissionStatus,
        submissionDueDate: participantSubmissionDueDate,
        questionnaireTemplateConfig
      } = participantQuestionnaire;

      const { supportedLanguages = null } = questionnaireTemplateConfig ? JSON.parse(questionnaireTemplateConfig) : {};

      const submissionDatePassed = currentDate > dateWithoutTime(participantSubmissionDueDate);

      const participantDateStatus =
        submissionDatePassed && OpenTasksSubmissionStatus.includes(participantSubmissionStatus)
          ? "warning"
          : submissionDatePassed && participantSubmissionStatus === SubmissionStatus.Submitted
          ? "success"
          : "normal";

      return (
        <div className={classes.feedbackCardAction}>
          <div className={classes.feedbackCardActionName}>
            {[TalkStatus.Cancelled].includes(talkStatus as TalkStatus) ? (
              <Typography className={classes.title} display="inline" variant="subtitle1">
                {t(`feedback.submitter.selfAssessment`)}
              </Typography>
            ) : (
              <Link
                to={`/${language}/dashboard/my-feedbacks/my-questionnaire/${questionnaireId}?${btoa(
                  qs.stringify(
                    { supported: [supportedLanguages] },
                    {
                      arrayFormat: "comma"
                    }
                  )
                )}`}
              >
                {t(`feedback.submitter.selfAssessment`)}
              </Link>
            )}
          </div>
          <div className={classes.mobileDates}>
            <Typography variant="body1" display="inline">
              {`${t(`feedback.page.my-feedbacks.respondent.me`)} `}
            </Typography>
            <DateStatus type={participantDateStatus} date={participantSubmissionDueDate} />
          </div>
          <div className={classes.statusWrapper}>
            <Status type="questionnaire" status={participantSubmissionStatus} />
          </div>
          <div className={classes.respondentWrapper}>
            <Typography className={classes.title} variant="body1" display="inline">
              {t(`feedback.page.my-feedbacks.respondent.me`)}
            </Typography>
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={participantDateStatus} date={participantSubmissionDueDate} />
          </div>
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [talkQuestionnaires, language]);

  /**
   * Calculates if the Peer feedback link should be disabled.
   * If the peerselection status is not submitted and the duedate has passsed
   * then the link should be disabled.
   */
  const getPeerSelectionLinkStatus = React.useCallback((peerSelectionStatus: PeerSelectionStatus) => {
    if ([PeerSelectionStatus.Cancelled].includes(peerSelectionStatus)) {
      return (
        <Typography className={classes.title} display="inline">
          {t(`feedback.submitter.peerSelection`, {})}
        </Typography>
      );
    } else {
      return (
        <Link
          dataTestId="feedbackCardPeerSelectionLink"
          to={`/${language}/dashboard/my-feedbacks/peer-selection/${talkId}`}
        >
          {t(`feedback.submitter.peerSelection`)}
        </Link>
      );
    }
  }, []);

  const PeerSelectionAction = React.useMemo(() => {
    if (peerSelection) {
      const { dueDate: peerSelectionDueDate, status: peerSelectionStatus } = peerSelection;

      const peerSelectionPassed = dateWithoutTime(currentDate) > dateWithoutTime(peerSelectionDueDate);

      const peerStatus =
        peerSelectionPassed && OpenTasksSubmissionStatus.includes(peerSelectionStatus)
          ? "warning"
          : peerSelectionPassed && peerSelectionStatus === PeerSelectionStatus.Submitted
          ? "success"
          : "normal";

      return (
        <div data-qa-selector="feedbackCardPeerSelection" className={classes.feedbackCardAction}>
          <div className={classes.feedbackCardActionName}>{getPeerSelectionLinkStatus(peerSelectionStatus)}</div>
          <div className={classes.mobileDates}>
            <Typography variant="body1" display="inline">
              {`${t(`feedback.page.my-feedbacks.respondent.me`)} `}
            </Typography>
            <DateStatus type={peerStatus} date={peerSelectionDueDate} />
          </div>
          <div className={classes.statusWrapper}>
            <Status type="peerSelection" status={peerSelectionStatus} />
          </div>
          <div className={classes.respondentWrapper}>
            <Typography className={classes.title} variant="body1" display="inline">
              {t(`feedback.page.my-feedbacks.respondent.me`)}
            </Typography>
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={peerStatus} date={peerSelectionDueDate} />
          </div>
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [peerSelection, language]);

  return (
    <div data-qa-selector="feedbackCard" key={talkId} className={classes.feedbackCardContainer}>
      <input defaultChecked id={talkCheckboxId} type="checkbox" className={classes.toggler} />
      {TalkSheetAction}
      <div className={classes.feedbackCardActions}>
        {MyQuestionnaireAction}
        {DSVQuesitonnaireAction}
        {PeerSelectionAction}
      </div>
    </div>
  );
};

export default React.memo(FeedbackCard);
